import { Mission } from '@/app/hooks/trade/tradeMission';
import BigNumber from 'bignumber.js';

export enum MissionCode {
  // onboarding
  SIGNUP_BONUS = 'SIGNUP_BONUS',
  KYC_COMPLETE = 'KYC_COMPLETE',
  FIRST_DEPOSIT_PROMO = 'FIRST_DEPOSIT_PROMO',
  APP_LOGIN_PROMO = 'APP_LOGIN_PROMO',

  // trade
  NET_DEPOSIT_GOAL = 'NET_DEPOSIT_GOAL',
  NET_VOLUME_GOAL = 'NET_VOLUME_GOAL',
  PERIOD_MISSION = 'PERIOD_MISSION',

  // feature
  TRIGGER_ORDER_FIRST = 'TRIGGER_ORDER_FIRST',
  INSTANT_CLOSE_FIRST = 'INSTANT_CLOSE_FIRST',
  TPSL_SETUP_FIRST = 'TPSL_SETUP_FIRST',
  LEVERAGE_FIRST = 'LEVERAGE_FIRST',
  LEVERAGE_GOAL = 'LEVERAGE_GOAL',
  BOT_FIRST_USE = 'BOT_FIRST_USE',
  BOT_VOLUME_GOAL = 'BOT_VOLUME_GOAL',
  DEMO_FIRST_USE = 'DEMO_FIRST_USE',
  DEMO_VOLUME_GOAL = 'DEMO_VOLUME_GOAL',
  COPY_FIRST_USE = 'COPY_FIRST_USE',
  COPY_VOLUME_GOAL = 'COPY_VOLUME_GOAL',
  REFERRAL_GOAL = 'REFERRAL_GOAL',

  // daily
  DAILY_ACTIVITY_GOAL = 'DAILY_ACTIVITY_GOAL',
  ATTENDANCE_STREAK = 'ATTENDANCE_STREAK',
}

export const getMissionTemplateCode = (code: string) => {
  const templateCode = code.replace(/_\d+$/, '') as MissionCode;
  return templateCode;
};

/**
 *
 * @param mission 미션 정보
 * @param index 상세 미션 정보의 tiers의 index
 * @returns canClaim, claimed, notAchieve
 */
export const getClaimStatus = (mission: Mission, index: number) => {
  if (!mission) return 'notAchieve';

  // userProgress가 없는 경우 (비로그인 상태)
  if (!mission.userProgress) return 'notAchieve';

  // 미션에 tiers가 있는 경우
  if (mission.userProgress?.totalTiers && mission.userProgress.totalTiers > 0) {
    const isAchieve = (mission.userProgress?.achievedTiers ?? []).includes(
      index
    );

    // 달성했고, 보상을 받지 않은 경우
    if (isAchieve && !mission.userProgress.claimedTiers?.includes(index)) {
      return 'canClaim';
    }

    // 달성했고, 보상을 받은 경우
    if (isAchieve && mission.userProgress.claimedTiers?.includes(index)) {
      return 'claimed';
    }

    // 달성하지 못한 경우
    return 'notAchieve';
  }

  // 아래는 미션에 tiers가 없는 경우

  // 달성했고, 보상을 받은 경우
  if (mission.userProgress?.claimedAt) {
    return 'claimed';
  }

  // 달성했고, 보상을 받지 않은 경우
  if (mission.userProgress?.canClaim) {
    return 'canClaim';
  }

  // 달성하지 못한 경우
  return 'notAchieve';
};

export const canClaimable = (mission: Mission, index: number) => {
  if (!mission) return false;
  return getClaimStatus(mission, index) === 'canClaim';
};

export const formatAmountWithUnit = (
  amount: number | string,
  decimalPlaces: number = 0
) => {
  const number = new BigNumber(amount);
  if (number.isLessThan(1000)) return number.toFormat(decimalPlaces);

  if (number.isLessThan(1000000))
    return number.dividedBy(1000).toFormat(decimalPlaces) + 'K';

  if (number.isLessThan(1000000000))
    return number.dividedBy(1000000).toFormat(decimalPlaces) + 'M';

  return number.dividedBy(1000000000).toFormat(decimalPlaces) + 'B';
};

const formatTime = (date: Date, isKo: boolean) => {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth(); // 0-11
  const day = date.getUTCDate();
  const hour = String(date.getUTCHours()).padStart(2, '0');
  const min = String(date.getUTCMinutes()).padStart(2, '0');

  if (isKo) {
    return `${year}년 ${month + 1}월 ${day}일 ${hour}:${min}`;
  } else {
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    return `${year} ${monthNames[month]} ${day} ${hour}:${min}`;
  }
};

export const formatTimezoneInfo = (
  detailPeriodMission: Mission,
  isKo: boolean
) => {
  if (!detailPeriodMission?.endDate)
    return { utc: '', kst: '', cst: '', est: '', pst: '' };

  const endDate = new Date(detailPeriodMission.endDate);

  const utc = formatTime(endDate, isKo);
  const kst = formatTime(
    new Date(endDate.getTime() + 9 * 60 * 60 * 1000),
    isKo
  ); // +9시간
  const cst = formatTime(
    new Date(endDate.getTime() + 8 * 60 * 60 * 1000),
    isKo
  ); // +8시간
  const est = formatTime(
    new Date(endDate.getTime() - 5 * 60 * 60 * 1000),
    isKo
  ); // -5시간
  const pst = formatTime(
    new Date(endDate.getTime() - 8 * 60 * 60 * 1000),
    isKo
  ); // -8시간

  return {
    utc,
    kst,
    cst,
    est,
    pst,
  };
};

export const hasTiers = (mission: Mission): boolean => {
  return !!(
    mission?.userProgress?.totalTiers && mission.userProgress.totalTiers > 0
  );
};

/**
 * 리워드 UI 표시 타입 정의
 * - 'default': 일반 수령 방식 (즉시 수령 버튼)
 * - 'lottery': 뽑기 방식 (3개 카드 중 선택)
 *
 * 주의: rewards.type(usdt, vip_upgrade, fee_discount)은 "지급 방식"이고,
 *       displayType은 "UI 표시 방식"으로 별개 개념입니다.
 */
export type RewardDisplayType = 'default' | 'lottery';

/**
 * 뽑기 UI를 적용할 미션 코드 목록
 * 프론트에서 직접 지정하여 빠르게 적용 (백엔드 수정 없이)
 *
 * 💡 백엔드 metadata.displayType으로 제어하려면 이 배열을 비워두세요.
 *    현재는 백엔드 API에서 metadata를 반환하도록 설정되어 있습니다.
 */
const LOTTERY_MISSION_CODES: MissionCode[] = [
  // 백엔드 metadata.displayType으로 제어
];

/**
 * 미션의 리워드 UI 표시 타입을 반환
 * 우선순위: 1) 프론트 하드코딩 → 2) metadata.displayType → 3) rewards.displayType
 */
export const getRewardDisplayType = (mission: Mission): RewardDisplayType => {
  // 1. 프론트에서 지정한 미션 코드 체크 (빠른 적용용)
  const templateCode = getMissionTemplateCode(mission?.code);
  if (LOTTERY_MISSION_CODES.includes(templateCode)) {
    return 'lottery';
  }

  // 2. mission metadata 내 displayType 체크 (Option 2 - 유연한 방식)
  const metadataDisplayType = mission?.metadata?.displayType;
  if (metadataDisplayType === 'lottery') return 'lottery';

  // 3. rewards 객체 내 displayType 체크 (Option 1 - 구조적 방식)
  const rewardsDisplayType = (mission?.rewards as any)?.displayType;
  if (rewardsDisplayType === 'lottery') return 'lottery';

  return 'default';
};

/**
 * 뽑기 타입 리워드인지 확인
 */
export const isLotteryReward = (mission: Mission): boolean => {
  return getRewardDisplayType(mission) === 'lottery';
};
